import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { sentryUtils } from "@/shared/utils/sentry";

import {
  MortgageForm,
  EMPTY_MORTGAGE_FORM,
  validateMortgageFields,
  mortgageToItemFields,
  type MortgageFormValues,
  type MortgageFieldErrors,
} from "./MortgageForm";

function buildInitialForm(answers: Record<string, string | string[]>): MortgageFormValues {
  const selectedProperty = answers["extracted_mortgages"];
  if (selectedProperty === "other") return EMPTY_MORTGAGE_FORM;

  const getString = (key: string): string => {
    const val = answers[key];
    return typeof val === "string" ? val : "";
  };

  const address = getString("prefill_property_address");
  const lender = getString("prefill_lender_name");
  const balance = getString("prefill_balance");

  if (!address && !lender && !balance) return EMPTY_MORTGAGE_FORM;

  return {
    ...EMPTY_MORTGAGE_FORM,
    propertyAddress: address,
    lenderName: lender,
    remainingBalance: balance,
  };
}

export const AddMortgage: FC<FlowCustomComponentProps> = ({ onComplete, answers, setAnswer }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, updateItem, isAddingItem, isUpdatingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    MortgageFormValues,
    MortgageFieldErrors
  >(() => buildInitialForm(answers));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const errors = validateMortgageFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (!estateId) return;

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let statementDocId: number | undefined;
      let contractDocId: number | undefined;

      if (form.statementFile) {
        statementDocId = await uploadDocumentWithAuth(
          form.statementFile,
          estateId,
          "mortgage_statement",
        );
      }

      if (form.contractFile) {
        contractDocId = await uploadDocumentWithAuth(
          form.contractFile,
          estateId,
          "mortgage_statement",
        );
      }

      const fields = mortgageToItemFields(form);
      const rawMortgageId = answers["prefill_mortgage_id"];
      const existingMortgageId =
        typeof rawMortgageId === "string" && rawMortgageId
          ? parseInt(rawMortgageId, 10)
          : undefined;
      const rawCreatedId = answers["created_mortgage_id"];
      const createdMortgageId =
        typeof rawCreatedId === "string" && rawCreatedId ? parseInt(rawCreatedId, 10) : undefined;
      const updateId = existingMortgageId ?? createdMortgageId;
      const rawLinkedPropertyId = answers["prefill_linked_property_id"];
      let propertyId =
        typeof rawLinkedPropertyId === "string" && rawLinkedPropertyId
          ? parseInt(rawLinkedPropertyId, 10)
          : undefined;

      if (!propertyId && form.propertyAddress.trim()) {
        const property = await addItem({
          estate_id: estateId,
          category: "Properties",
          classification: "asset",
          item_name: form.propertyAddress.trim(),
          balance: parseFloat(form.estimatedValue) || 0,
        });
        if (property?.id) {
          propertyId = property.id;
        }
      }

      if (updateId) {
        await updateItem(updateId, {
          item_name: fields.item_name,
          balance: fields.balance,
          metadata: fields.metadata,
          source_document_id: statementDocId ?? contractDocId,
          linked_item_id: propertyId ?? undefined,
        });
      } else {
        const result = await addItem({
          estate_id: estateId,
          category: "Secured debts",
          classification: "debt",
          item_name: fields.item_name,
          balance: fields.balance,
          metadata: fields.metadata,
          source_document_id: statementDocId ?? contractDocId,
          linked_item_id: propertyId ?? undefined,
        });
        if (result?.id) {
          setAnswer("created_mortgage_id", String(result.id));
        }
      }

      onComplete();
    } catch (err) {
      setError("Failed to add mortgage. Please try again.");
      sentryUtils.captureError(err, "AddMortgage submission failed", {
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showEstimatedValue = answers["has_properties"] === "none";

  return (
    <AddItemLayout
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      error={error}
      disabled={isAddingItem || isUpdatingItem}
    >
      <MortgageForm
        form={form}
        onChange={updateForm}
        fieldErrors={fieldErrors}
        showEstimatedValue={showEstimatedValue}
      />
    </AddItemLayout>
  );
};
